import React, { useContext, useMemo, useState } from 'react';
import EditText from '../crm/shared/field_inputs/text/EditText';
import EditNumber from '../crm/shared/field_inputs/number/EditNumber';
import { DataContext as SharedDataContext } from '../../stores/crm-store';
import { HandlePatchCourse } from './requests/CourseRequests';
import {  createGetTeacherSuggestionsFromApiUrl } from '../crm/shared/field_inputs/HelpMethods';
import { GetTeachersByIds } from './requests/TeacherRequests';
import { allowedRoleFormComponents, countStatuses, courseFormatComponentsFilter, courseCategoryComponentsFilter, courseDeterminePriceAndTravelSupplement, regularSpotOpen, reserveSpotOpen } from './CourseHelperFunctions';
import EditSelect from '../crm/shared/field_inputs/select/EditSelect';
import { bookingStatusOptions, courseCategoryOptions, courseFormatOptions, courseLanguageOptions, courseStatusOptions, getOptionsObjectValue, getOptionsObjectValues } from './shared/translations';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import ExlEditItemList from './field_inputs/item/ExlEditItemList';
import ExlEditNumber from './field_inputs/number/ExlEditNumber';
import ExlFormModal from './shared/ExlFormModal';
import ExlEditDraft from './field_inputs/ExlEditDraft';
import Checkbox from '../Checkbox';
import moment from 'moment'; 
import { useTranslation } from 'react-i18next';
import { COURSE_MODALS_ADDRESS_LABEL, COURSE_MODALS_BOOK_EXCEEDS_MAX_NUM_VALI, COURSE_MODALS_CATEGORY_LABEL, COURSE_MODALS_DESCRIPTION_LABEL, COURSE_MODALS_EMAIL_NOTIF_LABEL, COURSE_MODALS_FORMAT_LABEL, COURSE_MODALS_LABEL, COURSE_MODALS_LANG_LABEL, COURSE_MODALS_MAX_BOOKINGS_LABEL, COURSE_MODALS_MAX_RESERVES_LABEL, COURSE_MODALS_MEET_LINK_LABEL, COURSE_MODALS_NAME_LABEL, COURSE_MODALS_NO_ADDRESS_VALI, COURSE_MODALS_NO_CATE_VALI, COURSE_MODALS_NO_FORMAT_VALI, COURSE_MODALS_NO_LANG_VALI, COURSE_MODALS_NO_MAX_BOOK_VALI, COURSE_MODALS_NO_NAME_VALI, COURSE_MODALS_NO_STATUS_VALI, COURSE_MODALS_NO_TEACHER_VALI, COURSE_MODALS_RES_EXCEEDS_MAX_NUM_VALI, COURSE_MODALS_STATUS_LABEL, COURSE_MODALS_TEACHER_LABEL, COURSE_MODALS_TO_HIGH_MAX_BOOK_VALI, COURSE_MODALS_TO_HIGH_MAX_RES_VALI, COURSE_MODALS_TO_LONG_NAME_VALI, COURSE_MODALS_TO_LOW_MAX_BOOK_VALI, EDIT_COURSE_LABEL, COURSE_MODALS_PRICE_LABEL, COURSE_MODALS_TRAVEL_SUPPLEMENT_LABEL } from '../../i18n/keyConstants';

const EditCourse = ({ updateCourse, submitHandler, permissionRole, open, onClose }) => {
    const { dispatch: sharedDispatch } = useContext(SharedDataContext);
    const courseStatuses = getOptionsObjectValues(courseStatusOptions);
    const courseCategories = getOptionsObjectValues(courseCategoryOptions);
    const courseLanguages = getOptionsObjectValues(courseLanguageOptions);
    const courseFormats = getOptionsObjectValues(courseFormatOptions);
    const { t } = useTranslation(['courseModals', 'editCourse']);

    const defaultFormValues = useMemo(() => ({
        name: updateCourse.name,
        teachers: updateCourse.teachers,
        startDate: moment.utc(updateCourse.startDate).local(),
        endDate: moment.utc(updateCourse.endDate).local(),
        maxNumBookings: updateCourse.maxNumBookings,
        maxNumReserves: updateCourse.maxNumReserves,
        status: getOptionsObjectValue(courseStatusOptions, updateCourse.status),
        category: getOptionsObjectValue(courseCategoryOptions, updateCourse.category),
        language: getOptionsObjectValue(courseLanguageOptions, updateCourse.language),
        location: updateCourse.location,
        meetingLink: updateCourse.meetingLink,
        courseFormat: getOptionsObjectValue(courseFormatOptions, updateCourse.courseFormat),
        description: updateCourse.description,
        price: updateCourse.price,
        travelSupplement: updateCourse.travelSupplement,
        sendEmailNotifications: updateCourse.sendEmailNotifications
    }), []);

    const formComponents = [
        {
            propName: 'name',
            label: t(COURSE_MODALS_NAME_LABEL),
            validate: (v) => (!v || !v.length ? t(COURSE_MODALS_NO_NAME_VALI) : v.length > 60 ? t(COURSE_MODALS_TO_LONG_NAME_VALI) : ''),
            component: <EditText />,
        },
        {
            propName: 'teachers',
            label: t(COURSE_MODALS_TEACHER_LABEL),
            validate: (v) => (!v || v.length < 1 ? t(COURSE_MODALS_NO_TEACHER_VALI) : ''),
            component:
                <ExlEditItemList
                    iconName='teacher'
                    isClearable
                    getSuggestions={createGetTeacherSuggestionsFromApiUrl('/api/exlTeacherFront/getTeacherSuggestions', updateCourse.id)} />
        },
        {
            propName: 'description',
            label: t(COURSE_MODALS_DESCRIPTION_LABEL),
            component: <ExlEditDraft />
        },
        {
            propName: 'maxNumBookings',
            label: t(COURSE_MODALS_MAX_BOOKINGS_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_MAX_BOOK_VALI)
                : v < countStatuses(updateCourse.bookings, bookingStatusOptions.Confirmed.value) ? t(COURSE_MODALS_BOOK_EXCEEDS_MAX_NUM_VALI)
                    : v > 100 ? t(COURSE_MODALS_TO_HIGH_MAX_BOOK_VALI)
                        : v <= 0 ? t(COURSE_MODALS_TO_LOW_MAX_BOOK_VALI)
                            : ''),
            component: <ExlEditNumber placeholder={0} />,
        },
        {
            propName: 'maxNumReserves',
            label: t(COURSE_MODALS_MAX_RESERVES_LABEL),
            validate: (v) => (v < countStatuses(updateCourse.bookings, bookingStatusOptions.Reserve.value) ? t(COURSE_MODALS_RES_EXCEEDS_MAX_NUM_VALI)
                : v > 100 ? t(COURSE_MODALS_TO_HIGH_MAX_RES_VALI)
                    : ''),
            component: <ExlEditNumber placeholder={0} />,
        },
        {
            propName: 'status',
            label: t(COURSE_MODALS_STATUS_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_STATUS_VALI) : ''),
            component: <EditSelect options={courseStatuses} iconName={'chevron-down'} />
        },
        {
            propName: 'category',
            label: t(COURSE_MODALS_CATEGORY_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_CATE_VALI) : ''),
            component: <EditSelect options={courseCategories} iconName={'chevron-down'} />
        },
        {
            propName: 'language',
            label: t(COURSE_MODALS_LANG_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_LANG_VALI) : ''),
            component: <EditSelect options={courseLanguages} iconName={'chevron-down'} />
        },
        {
            propName: 'courseFormat',
            label: t(COURSE_MODALS_FORMAT_LABEL),
            validate: (v) => (!v ? t(COURSE_MODALS_NO_FORMAT_VALI) : ''),
            component: <EditSelect options={courseFormats} iconName={'chevron-down'} />
        },
        {
            propName: 'location',
            label: t(COURSE_MODALS_ADDRESS_LABEL),
            validate: (v) => (!v || !v.length ? t(COURSE_MODALS_NO_ADDRESS_VALI) : ''),
            component: <EditText />,
        },
        {
            propName: 'meetingLink',
            label: t(COURSE_MODALS_MEET_LINK_LABEL),
            component: <EditText />,
        },
        {
            propName: 'price',
            propType: 'economy',
            label: t(COURSE_MODALS_PRICE_LABEL),
            component: <ExlEditNumber placeholder={0} />
        },
        {
            propName: 'travelSupplement',
            propType: 'economy',
            label: t(COURSE_MODALS_TRAVEL_SUPPLEMENT_LABEL),
            component: <ExlEditNumber placeholder={0} />
        },
        {
            propName: 'sendEmailNotifications',
            label: '',
            component:
                <Checkbox
                    usedInExl={true}
                    defaultChecked={updateCourse.sendEmailNotifications}
                    label={t(COURSE_MODALS_EMAIL_NOTIF_LABEL)}
                />
        },

    ];

    const filterFormComponents = (formValues) => {

        const filteredFormComponents = formComponents.filter((component) => {
            const isAllowed = allowedRoleFormComponents(permissionRole, formComponents).includes(component.propName);
            const allowedCourseFormatComp = courseFormatComponentsFilter(formValues?.courseFormat?.value, component);
            const allowedCourseCategoryComp = courseCategoryComponentsFilter(formValues?.category?.value, component);

            return (isAllowed && allowedCourseFormatComp && allowedCourseCategoryComp);
        });
        return filteredFormComponents;
    }


    const populateCourseObject = async (initialCourse, course) => ({
        ...initialCourse,
        ...course,
    });

    const handleAdd = async (course) => {
        course.id = updateCourse.id;
        const populatedCourse = await populateCourseObject(updateCourse, course);
        const teacherIds = course.teachers.map((teacher) => teacher.id);
        populatedCourse.teachers = await GetTeachersByIds(teacherIds);
        populatedCourse.teachers.forEach((t) => {
            delete t.courses;
            delete t.receiveEmailNotifications;
        })
        populatedCourse.numBookings = updateCourse.numBookings;
        populatedCourse.status = populatedCourse.status.value;
        populatedCourse.category = populatedCourse.category.value;
        populatedCourse.language = populatedCourse.language.value;
        populatedCourse.courseFormat = populatedCourse.courseFormat.value;

        courseDeterminePriceAndTravelSupplement(populatedCourse);

        switch (populatedCourse.courseFormat) {
            case 0:
                populatedCourse.location = null;
                break;
            case 1:
                populatedCourse.meetingLink = null;
                break;
        }

        var response = await HandlePatchCourse(updateCourse, populatedCourse);
        return response;
    };

    return (
        <ExlFormModal
            open={open}
            onClose={onClose}
            description={t(EDIT_COURSE_LABEL, {ns: 'editCourse'})}
            defaultFormValues={defaultFormValues}
            defaultFormComponents={formComponents}
            handleAdd={handleAdd}
            onSuccessiveSave={() => submitHandler()}
            callback={filterFormComponents}
            isExl
        />
    );
};

export default EditCourse;
